/* =========================================================
   Aether Rift — Rift Finder
   Instant catalog search + faceted filters, built on the compact
   JSON feed in templates/collection.data.liquid.
   Exposes window.RiftFinder for the header search, homepage rows and deck builder.
   ========================================================= */
(function () {
  'use strict';

  const AR = () => window.AetherRift || {};
  const CFG = () => AR().finder || {};
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

  /* ---------- helpers ---------- */
  const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const handle = (s) => norm(s).replace(/ /g, '-');
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const list = (str) => String(str || '').split(',').map((x) => x.trim()).filter(Boolean);
  const pairs = (str) => list(str).map((e) => { const [n, t] = e.split('='); return { name: n.trim(), tag: (t || n).trim() }; });

  function money(cents) {
    const fmt = AR().moneyFormat || '${{amount}}';
    const value = (Number(cents) || 0) / 100;
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return Math.round(value).toLocaleString('en-US');
      if (key === 'amount_with_comma_separator') return value.toFixed(2).replace('.', ',');
      return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    });
  }

  function condCode(v) {
    const c = String(v || '').toLowerCase().trim();
    if (/^(near mint|nm|mint|nm\/m|nm-m)/.test(c)) return ['NM', 'nm'];
    if (/^(lightly played|lp|excellent|ex)$/.test(c)) return ['LP', 'lp'];
    if (/^(moderately played|mp|good|gd)$/.test(c)) return ['MP', 'mp'];
    if (/^(heavily played|hp|played|pl)$/.test(c)) return ['HP', 'hp'];
    if (/^(damaged|dmg|poor)$/.test(c)) return ['DMG', 'dmg'];
    if (/sealed|unopened|new/.test(c)) return ['Sealed', 'sealed'];
    return [String(v || '').slice(0, 6), 'nm'];
  }

  function lev(a, b) {
    if (Math.abs(a.length - b.length) > 2) return 9;
    const m = []; for (let i = 0; i <= b.length; i++) m[i] = [i];
    for (let j = 0; j <= a.length; j++) m[0][j] = j;
    for (let i = 1; i <= b.length; i++) for (let j = 1; j <= a.length; j++) {
      m[i][j] = b[i - 1] === a[j - 1] ? m[i - 1][j - 1] : Math.min(m[i - 1][j - 1] + 1, m[i][j - 1] + 1, m[i - 1][j] + 1);
    }
    return m[b.length][a.length];
  }

  async function pool(items, size, fn) {
    const out = []; let i = 0;
    await Promise.all(Array.from({ length: Math.min(size, items.length) }, async () => {
      while (i < items.length) { const k = i++; out[k] = await fn(items[k], k); }
    }));
    return out;
  }

  /* ---------- catalog feed ---------- */
  const memo = new Map();
  const store = {
    get(k) { try { return JSON.parse(localStorage.getItem(k)); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { try { localStorage.removeItem(k); } catch (e2) { /* ignore */ } } }
  };
  const feedUrl = (base, view, page, sort) => {
    const u = new URL(base, window.location.origin);
    u.searchParams.set('view', view);
    u.searchParams.set('page', page);
    if (sort) u.searchParams.set('sort_by', sort);
    return u.pathname + u.search;
  };
  async function getPage(base, view, page, sort) {
    const res = await fetch(feedUrl(base, view, page, sort), { credentials: 'same-origin' });
    if (!res.ok) throw new Error('feed ' + res.status);
    return JSON.parse(await res.text());
  }

  /**
   * Load products from a collection / tag URL.
   * opts: { sort, maxPages, cache: true }
   */
  function fetchFeed(base, opts = {}) {
    const sort = opts.sort || '';
    const maxPages = opts.maxPages || 80;
    const key = 'ar:feed:v2:' + base + '|' + sort + '|' + maxPages;
    if (memo.has(key)) return memo.get(key);
    const ttl = (CFG().cacheMinutes || 10) * 60000;
    const p = (async () => {
      if (opts.cache !== false) {
        const hit = store.get(key);
        if (hit && Date.now() - hit.t < ttl && Array.isArray(hit.items)) return hit.items.map(enrich);
      }
      // A tag page is a subset of the full catalog: reuse it when it's already cached.
      const tagMatch = base.match(/\/collections\/all\/([^/?#]+)/);
      if (tagMatch && !sort) {
        const allKey = 'ar:feed:v2:' + CFG().feed + '||80';
        const all = memo.has(allKey) ? await memo.get(allKey).catch(() => null) : (store.get(allKey) && Date.now() - store.get(allKey).t < ttl ? store.get(allKey).items.map(enrich) : null);
        if (all) { const th = decodeURIComponent(tagMatch[1]); return all.filter((it) => it.tags.some((t) => handle(t) === th)); }
      }
      let view = 'data', first;
      try { first = await getPage(base, view, 1, sort); } catch (e) { view = 'data50'; first = await getPage(base, view, 1, sort); }
      const pages = Math.min(first.pages || 1, maxPages);
      const rest = await pool(Array.from({ length: Math.max(0, pages - 1) }, (_, i) => i + 2), 4, (n) => getPage(base, view, n, sort).then((j) => j.items).catch(() => []));
      const raw = first.items.concat(...rest);
      if (opts.cache !== false) store.set(key, { t: Date.now(), items: raw });
      return raw.map(enrich);
    })();
    memo.set(key, p);
    p.catch(() => memo.delete(key));
    return p;
  }
  const loadAll = () => fetchFeed(CFG().feed || '/collections/all');

  /* ---------- enrichment ---------- */
  let lookups = null;
  function getLookups() {
    if (lookups) return lookups;
    const c = CFG();
    const games = pairs(c.games);
    const supplies = pairs(c.supplies);
    const rarity = list(c.rarity);
    lookups = {
      gameByTag: new Map(games.map((g) => [norm(g.tag), g.name])),
      gameOrder: games.map((g) => g.name),
      supplyByTag: new Map(supplies.map((g) => [norm(g.tag), g.name])),
      suppliesTag: norm(c.suppliesTag || 'Supplies'),
      rarity: new Map(rarity.map((r, i) => [norm(r), i])),
      rarityLabel: new Map(rarity.map((r) => [norm(r), r])),
      colors: new Map(list(c.colors).map((r) => [norm(r), r])),
      types: new Map(list(c.types).map((r) => [norm(r), r]))
    };
    return lookups;
  }

  function enrich(raw) {
    if (raw._e) return raw;
    const L = getLookups();
    const it = Object.assign({}, raw, { _e: true });
    it.title = raw.t;
    it.url = raw.u;
    it.tags = raw.tg || [];
    const br = String(raw.t).match(/\[([^\]]+)\]\s*$/);
    it.name = String(raw.t).split(' [')[0].trim();
    if (br) { const parts = br[1].split(' - '); it.code = parts[0].trim(); it.num = parts.slice(1).join(' - ').trim(); } else { it.code = ''; it.num = ''; }
    const tn = it.tags.map(norm);
    it.game = '';
    for (const t of tn) if (L.gameByTag.has(t)) { it.game = L.gameByTag.get(t); break; }
    if (!it.game && raw.ty) it.game = String(raw.ty).replace(/\s+(singles|sealed|supplies)$/i, '').replace(/:.*$/, '');
    it.supply = '';
    for (const t of tn) if (L.supplyByTag.has(t)) { it.supply = L.supplyByTag.get(t); break; }
    it.isSupply = !!it.supply || tn.includes(L.suppliesTag) || /supplies|accessor/i.test(raw.ty || '');
    if (it.isSupply) it.game = '';
    it.set = it.isSupply ? '' : (raw.v || '');
    let best = null;
    tn.forEach((t, i) => { if (L.rarity.has(t) && (best === null || L.rarity.get(t) > L.rarity.get(norm(best)))) best = it.tags[i]; });
    it.rarity = best ? L.rarityLabel.get(norm(best)) : '';
    it.colors = tn.filter((t) => L.colors.has(t)).map((t) => L.colors.get(t));
    it.types = tn.filter((t) => L.types.has(t)).map((t) => L.types.get(t));
    const on = (raw.on || []).map((o) => String(o).toLowerCase());
    const opt = (re) => { const i = on.findIndex((o) => re.test(o)); return i === -1 ? '' : (raw.vo || [])[i] || ''; };
    it.cond = opt(/condition/);
    it.lang = opt(/language/);
    it.print = opt(/printing|finish|foil/);
    if (!it.print && /\bfoil\b/i.test(raw.t)) it.print = 'Foil';
    it.price = raw.p;
    it.avail = !!raw.a;
    it.sealed = /sealed|booster|box|bundle|deck|tin|pack/i.test(raw.ty || '') && !/singles/i.test(raw.ty || '');
    it.nameN = norm(it.name);
    it.codeN = norm(it.code + ' ' + it.num);
    it.hay = norm([it.name, it.code, it.num, it.set, it.game, it.supply, it.tags.join(' '), raw.sku, raw.ty, it.print, it.lang].join(' '));
    it.words = it.hay.split(' ');
    return it;
  }

  /* ---------- search ---------- */
  function matchItem(it, toks, nq) {
    for (const t of toks) {
      const ok = /\d/.test(t) ? it.hay.includes(t) : it.words.some((w) => w.startsWith(t));
      if (!ok) return 0;
    }
    if (!toks.length) return 1;
    const nameWords = it.nameN.split(' ');
    let s = 10;
    if (it.nameN === nq) s = 100;
    else if (it.codeN.includes(nq) && /\d/.test(nq)) s = 95;
    else if (it.nameN.startsWith(nq)) s = 80;
    else if (toks.every((t) => nameWords.some((w) => w.startsWith(t)))) s = 60;
    else if (toks.every((t) => it.words.includes(t) || nameWords.some((w) => w.startsWith(t)))) s = 40; // whole words: game, set, rarity, color…
    return s + (it.avail ? 3 : 0);
  }

  function search(items, q) {
    const nq = norm(q);
    const toks = nq.split(' ').filter(Boolean);
    if (!toks.length) return { results: items.slice(), scores: null, suggestion: '', loose: false };
    const scores = new Map();
    let results = [];
    items.forEach((it) => { const s = matchItem(it, toks, nq); if (s) { scores.set(it, s); results.push(it); } });
    // Prefer real matches: drop loose "partial word somewhere" hits when better ones exist.
    const strong = results.filter((it) => scores.get(it) >= 40);
    let loose = false;
    if (strong.length) results = strong; else if (results.length) loose = true;
    let suggestion = '';
    if (!strong.length) {
      // typo help: swap each unknown word for the closest card-name word we stock (same first letter)
      const vocab = new Map();
      items.forEach((it) => it.nameN.split(' ').concat(norm(it.set).split(' '), norm(it.game).split(' ')).forEach((w) => { if (w.length > 2) vocab.set(w, (vocab.get(w) || 0) + 1); }));
      const fixed = toks.map((t) => {
        if (t.length < 4 || vocab.has(t) || /\d/.test(t)) return t;
        let best = t, bd = t.length >= 7 ? 3 : 2;
        vocab.forEach((n, w) => {
          if (w[0] !== t[0]) return;
          const d = lev(t, w);
          if (d < bd || (d === bd && best !== t && n > (vocab.get(best) || 0))) { bd = d; best = w; }
        });
        return best;
      });
      const fq = fixed.join(' ');
      if (fq !== nq && search.__depth !== 1) {
        search.__depth = 1;
        const check = search(items, fq);
        search.__depth = 0;
        if (check.results.length && !check.loose) suggestion = fq;
      }
    }
    return { results, scores, suggestion, loose };
  }

  /* ---------- card rendering ---------- */
  function cardHTML(it, o = {}) {
    const cond = it.cond ? condCode(it.cond) : null;
    const foil = /foil|holo|etched/i.test(it.print) && !/non/i.test(it.print);
    const sale = it.c && it.c > it.p;
    const pre = /\bpre-?order\b/i.test(it.title) || it.tags.some((t) => /^pre-?order$/i.test(t));
    const img = it.img ? `<img src="${esc(it.img)}" alt="${esc(it.name)}" loading="${o.eager ? 'eager' : 'lazy'}" width="420" height="586">` : '<span class="card__noimg"></span>';
    const meta = [it.code && it.num ? `${esc(it.code)} · ${esc(it.num)}` : esc(it.code || it.set), it.rarity && `<span class="card__rarity">${esc(it.rarity)}</span>`].filter(Boolean).join('');
    const sub = [it.cond, it.print && it.print !== 'Normal' ? it.print : '', it.lang && it.lang !== 'English' ? it.lang : ''].filter(Boolean).join(' · ');
    let buy;
    if (!it.avail) buy = `<button type="button" class="btn btn--full btn--small btn--soldout" disabled>${esc(AR().strings && AR().strings.soldOut || 'Sold out')}</button>`;
    else if (it.vs && it.vs.length > 1) {
      const opts = it.vs.filter((v) => v[3]).map((v) => `<option value="${v[0]}">${esc(v[1].join(' / '))} — ${money(v[2])}</option>`).join('');
      buy = `<form data-ajax-add class="card__form"><select name="id" class="card__variant" aria-label="Version">${opts}</select><input type="hidden" name="quantity" value="1"><button type="submit" class="btn btn--full btn--small ${pre ? 'btn--gold' : 'btn--primary'}">${pre ? 'Pre-order' : 'Add to cart'}</button></form>`;
    } else {
      buy = `<form data-ajax-add><input type="hidden" name="id" value="${it.vid}"><input type="hidden" name="quantity" value="1"><button type="submit" class="btn btn--full btn--small ${pre ? 'btn--gold' : 'btn--primary'}" aria-label="Add ${esc(it.title)} to cart">${pre ? 'Pre-order' : 'Add to cart'}${cond ? ` · <span class="cond cond--${cond[1]}">${cond[0]}</span>` : ''}</button></form>`;
    }
    const badges = [pre && `<span class="badge badge--preorder">${esc(CFG().preorderLabel || 'Pre-order')}</span>`, foil && `<span class="badge badge--foil">${esc(it.print)}</span>`, sale && '<span class="badge badge--sale">Sale</span>', !it.avail && '<span class="badge badge--soldout">Sold out</span>'].filter(Boolean).join('');
    const market = it.mk ? `<span class="card__market">Market ${money(Math.round(parseFloat(it.mk) * 100))}</span>` : '';
    return `<div data-item data-product-id="${it.id}" data-name="${esc(it.title)}" data-price="${it.p}" data-available="${it.avail}">
  <article class="card${it.isSupply ? ' card--accessory' : ''}"${CFG().tilt && !it.isSupply ? ' data-tilt' : ''}>
    <a class="card__media" href="${esc(it.url)}" tabindex="-1" aria-hidden="true">${img}<span class="card__holo"></span></a>
    <div class="card__badges">${badges}</div>
    <div class="card__body">
      <div class="card__price"><span class="price">${money(it.p)}</span>${sale ? `<s class="card__was">${money(it.c)}</s>` : ''}${market}</div>
      <h3 class="card__title"><a href="${esc(it.url)}" title="${esc(it.title)}">${esc(it.name)}</a></h3>
      ${meta ? `<div class="card__meta">${meta}</div>` : ''}
      ${o.list && sub ? `<div class="card__sub">${esc(sub)}</div>` : ''}
      ${o.showGame && it.game ? `<div class="card__game">${esc(it.game)}</div>` : ''}
      <div class="card__buy">${buy}</div>
    </div>
  </article>
</div>`;
  }

  function afterCards(scope) {
    const ui = window.AetherRiftUI;
    if (ui && ui.initTilt) ui.initTilt(scope);
  }

  /* ---------- facets ---------- */
  const FACETS = [
    { key: 'game', label: 'Game', get: (i) => (i.game ? [i.game] : []), order: () => getLookups().gameOrder },
    { key: 'cat', label: 'Category', get: (i) => [i.isSupply ? 'Supplies' : i.sealed ? 'Sealed' : 'Singles'] },
    { key: 'set', label: 'Set', get: (i) => (i.set ? [i.set] : []), findable: true, more: 8 },
    { key: 'rarity', label: 'Rarity', get: (i) => (i.rarity ? [i.rarity] : []), order: () => list(CFG().rarity) },
    { key: 'color', label: 'Color / type', get: (i) => i.colors, more: 10 },
    { key: 'type', label: 'Card type', get: (i) => i.types, more: 10 },
    { key: 'supply', label: 'Supplies', get: (i) => (i.supply ? [i.supply] : []) },
    { key: 'print', label: 'Printing', get: (i) => (i.print ? [i.print] : []) },
    { key: 'cond', label: 'Condition', get: (i) => (i.cond ? [i.cond] : []), order: () => ['Near Mint', 'Lightly Played', 'Moderately Played', 'Heavily Played', 'Damaged'] },
    { key: 'lang', label: 'Language', get: (i) => (i.lang ? [i.lang] : []) }
  ];
  const SORTS = [
    ['relevance', 'Best match'], ['featured', 'Featured'], ['price-desc', 'Price: high to low'], ['price-asc', 'Price: low to high'],
    ['name', 'Name A–Z'], ['newest', 'Newest'], ['set', 'Set & card number']
  ];
  const PRICE_CHIPS = [['', 100, 'Under $1'], [100, 500, '$1 – $5'], [500, 2000, '$5 – $20'], [2000, 5000, '$20 – $50'], [5000, '', '$50+']];

  function sorter(mode, scores) {
    const byName = (a, b) => collator.compare(a.name, b.name);
    switch (mode) {
      case 'price-desc': return (a, b) => b.p - a.p || byName(a, b);
      case 'price-asc': return (a, b) => a.p - b.p || byName(a, b);
      case 'name': return byName;
      case 'newest': return (a, b) => (b.d || 0) - (a.d || 0);
      case 'set': return (a, b) => collator.compare(a.set, b.set) || collator.compare(a.num || '~', b.num || '~');
      case 'relevance': return scores ? (a, b) => (scores.get(b) - scores.get(a)) || (b.avail - a.avail) || b.p - a.p : null;
      default: return null;
    }
  }

  /* ---------- the Finder UI ---------- */
  function readState(root) {
    const sp = new URLSearchParams(window.location.search);
    const f = {};
    FACETS.forEach((fc) => { const v = sp.get(fc.key); if (v) f[fc.key] = new Set(v.split('~').filter(Boolean)); });
    return {
      q: sp.has('q') ? sp.get('q') : (root.dataset.query || ''),
      f,
      min: sp.get('min') || '',
      max: sp.get('max') || '',
      stock: sp.get('stock') === '1' || (sp.get('stock') === null && root.dataset.stockDefault === '1'),
      sort: sp.get('sort') || '',
      page: Math.max(1, Number(sp.get('pg') || 1)),
      view: sp.get('view_mode') || (() => { try { return localStorage.getItem('ar:finder:view') || 'grid'; } catch (e) { return 'grid'; } })()
    };
  }

  function writeState(root, st) {
    const u = new URL(window.location.href);
    ['q', 'min', 'max', 'stock', 'sort', 'pg', 'view_mode'].concat(FACETS.map((f) => f.key)).forEach((k) => u.searchParams.delete(k));
    if (root.dataset.mode === 'search' || st.q) { if (st.q) u.searchParams.set('q', st.q); }
    FACETS.forEach((fc) => { const s = st.f[fc.key]; if (s && s.size) u.searchParams.set(fc.key, Array.from(s).join('~')); });
    if (st.min) u.searchParams.set('min', st.min);
    if (st.max) u.searchParams.set('max', st.max);
    if (st.stock && root.dataset.stockDefault !== '1') u.searchParams.set('stock', '1');
    if (!st.stock && root.dataset.stockDefault === '1') u.searchParams.set('stock', '0');
    if (st.sort) u.searchParams.set('sort', st.sort);
    if (st.page > 1) u.searchParams.set('pg', st.page);
    history.replaceState(history.state, '', u.pathname + u.search);
  }

  async function mount(root) {
    if (root.dataset.finderReady) return;
    root.dataset.finderReady = '1';
    const source = root.dataset.source || CFG().feed || '/collections/all';
    const isSearch = root.dataset.mode === 'search';
    const perPage = Number(root.dataset.perPage || CFG().perPage || 48);
    const st = readState(root);

    root.innerHTML = `
      <div class="finder__top">
        <form class="finder__search" role="search" data-f-form>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <label class="visually-hidden" for="FinderQ-${root.id}">Search</label>
          <input id="FinderQ-${root.id}" type="search" autocomplete="off" spellcheck="false" placeholder="${esc(root.dataset.placeholder || 'Search by card name, set, code (OP14-089) or number…')}" value="${esc(st.q)}" data-f-q>
          <button type="button" class="finder__clearq" data-f-clearq aria-label="Clear search" hidden>✕</button>
        </form>
        <div class="finder__bar">
          <button type="button" class="fbar__btn finder__filters-btn" data-f-open aria-controls="FinderSide-${root.id}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true" width="16" height="16"><path d="M4 6h16M7 12h10M10 18h4"/></svg> Filters <span class="fbar__count" data-f-nactive hidden></span></button>
          <span class="finder__count" data-f-count aria-live="polite">Loading the vault…</span>
          <div class="finder__bar-right">
            <label class="fbar__toggle finder__stock"><input type="checkbox" data-f-stock ${st.stock ? 'checked' : ''}> In stock</label>
            <label class="visually-hidden" for="FinderSort-${root.id}">Sort</label>
            <select class="fbar__select" id="FinderSort-${root.id}" data-f-sort></select>
            <div class="view-toggle" role="group" aria-label="View">
              <button type="button" data-f-view="grid" aria-pressed="${st.view !== 'list'}">Grid</button>
              <button type="button" data-f-view="list" aria-pressed="${st.view === 'list'}">List</button>
            </div>
          </div>
        </div>
        <div class="finder__chips" data-f-chips></div>
        <div class="finder__note" data-f-note hidden></div>
      </div>
      <div class="finder__layout">
        <aside class="finder__side" id="FinderSide-${root.id}" data-f-side aria-label="Filters">
          <div class="finder__side-head"><strong>Filters</strong><button type="button" class="icon-btn" data-f-close aria-label="Close filters">✕</button></div>
          <div data-f-facets><div class="finder__skeleton"></div></div>
          <div class="finder__side-foot"><button type="button" class="btn btn--primary btn--full" data-f-close>Show results</button></div>
        </aside>
        <div class="finder__main">
          <div class="product-list grid finder__grid" data-view="${st.view === 'list' ? 'list' : 'grid'}" data-f-grid style="--cols: ${root.dataset.cols || 4}; --cols-tablet: 3; --cols-mobile: 2;">
            ${Array.from({ length: 8 }, () => '<div class="card card--skeleton" aria-hidden="true"><div class="card__media"></div><div class="card__body"><span></span><span></span></div></div>').join('')}
          </div>
          <nav class="pagination" data-f-pages aria-label="Pagination"></nav>
        </div>
      </div>
      <div class="finder__scrim" data-f-close></div>`;

    let items;
    try {
      items = await fetchFeed(source);
    } catch (e) {
      root.classList.add('is-failed');
      const fb = document.querySelector(root.dataset.fallback || '[data-finder-fallback]');
      if (fb) { fb.classList.add('is-shown'); root.hidden = true; }
      return;
    }
    root.classList.add('is-live');

    const els = {
      q: $('[data-f-q]', root), clearq: $('[data-f-clearq]', root), count: $('[data-f-count]', root), sort: $('[data-f-sort]', root),
      stock: $('[data-f-stock]', root), chips: $('[data-f-chips]', root), note: $('[data-f-note]', root), facets: $('[data-f-facets]', root),
      grid: $('[data-f-grid]', root), pages: $('[data-f-pages]', root), side: $('[data-f-side]', root), nactive: $('[data-f-nactive]', root)
    };
    const openFacets = new Set(['game', 'set', 'rarity', 'cond']);
    const showAll = new Set();
    const facetFind = {};

    function passes(it, skipKey, min, max) {
      for (const fc of FACETS) {
        if (fc.key === skipKey) continue;
        const sel = st.f[fc.key];
        if (sel && sel.size && !fc.get(it).some((v) => sel.has(v))) return false;
      }
      if (st.stock && !it.avail) return false;
      if (min !== null && it.p < min) return false;
      if (max !== null && it.p > max) return false;
      return true;
    }

    function render(opts = {}) {
      const { results, scores, suggestion, loose } = search(items, st.q);
      const min = st.min === '' ? null : Math.round(Number(st.min) * 100);
      const max = st.max === '' ? null : Math.round(Number(st.max) * 100);
      const filtered = results.filter((it) => passes(it, null, min, max));
      const mode = st.sort || (st.q ? 'relevance' : (root.dataset.defaultSort || 'featured'));
      const cmp = sorter(mode, scores);
      if (cmp) filtered.sort(cmp);

      // sort options
      els.sort.innerHTML = SORTS.filter(([k]) => k !== 'relevance' || st.q).map(([k, l]) => `<option value="${k}" ${k === mode ? 'selected' : ''}>Sort: ${l}</option>`).join('');

      // facets (counts ignore the facet's own selection)
      const html = FACETS.map((fc) => {
        const counts = new Map();
        results.forEach((it) => { if (!passes(it, fc.key, min, max)) return; fc.get(it).forEach((v) => counts.set(v, (counts.get(v) || 0) + 1)); });
        const sel = st.f[fc.key] || new Set();
        sel.forEach((v) => { if (!counts.has(v)) counts.set(v, 0); });
        if (counts.size < 2 && !sel.size) return '';
        let vals = Array.from(counts.keys());
        const ord = fc.order ? fc.order() : null;
        if (ord && ord.length) vals.sort((a, b) => { const ia = ord.indexOf(a), ib = ord.indexOf(b); return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib) || collator.compare(a, b); });
        else vals.sort((a, b) => counts.get(b) - counts.get(a) || collator.compare(a, b));
        const find = (facetFind[fc.key] || '').toLowerCase();
        if (find) vals = vals.filter((v) => v.toLowerCase().includes(find));
        const limit = fc.more && !showAll.has(fc.key) && !find ? fc.more : Infinity;
        const shown = vals.slice(0, limit);
        const n = sel.size;
        return `<details class="ffacet" data-facet="${fc.key}" ${openFacets.has(fc.key) || n ? 'open' : ''}>
          <summary>${esc(fc.label)}${n ? ` <span class="fbar__count">${n}</span>` : ''}</summary>
          ${fc.findable && counts.size > 10 ? `<input class="input ffacet__find" type="search" placeholder="Find a ${esc(fc.label.toLowerCase())}…" value="${esc(facetFind[fc.key] || '')}" data-f-find="${fc.key}" aria-label="Find ${esc(fc.label)}">` : ''}
          <ul class="ffacet__list">${shown.map((v) => {
            const c = counts.get(v);
            const cc = fc.key === 'cond' ? condCode(v) : null;
            return `<li><label class="${c === 0 && !sel.has(v) ? 'is-disabled' : ''}"><input type="checkbox" data-f-facet="${fc.key}" value="${esc(v)}" ${sel.has(v) ? 'checked' : ''} ${c === 0 && !sel.has(v) ? 'disabled' : ''}>${cc ? `<span class="cond cond--${cc[1]}">${cc[0]}</span>` : ''}<span class="ffacet__label">${esc(v)}</span><span class="count">${c}</span></label></li>`;
          }).join('')}</ul>
          ${vals.length > shown.length ? `<button type="button" class="ffacet__more" data-f-more="${fc.key}">Show all ${vals.length}</button>` : ''}
        </details>`;
      }).join('');
      const priceSel = (a, b) => String(st.min === '' ? '' : Math.round(st.min * 100)) === String(a) && String(st.max === '' ? '' : Math.round(st.max * 100)) === String(b);
      els.facets.innerHTML = html + `<details class="ffacet" data-facet="price" ${st.min || st.max ? 'open' : 'open'}>
          <summary>Price</summary>
          <div class="ffacet__chips">${PRICE_CHIPS.map(([a, b, l]) => `<button type="button" class="chip${priceSel(a, b) ? ' is-active' : ''}" data-f-price="${a}|${b}">${l}</button>`).join('')}</div>
          <div class="facet__price"><input class="input" type="number" min="0" step="0.01" placeholder="Min $" value="${esc(st.min)}" data-f-min aria-label="Minimum price"><input class="input" type="number" min="0" step="0.01" placeholder="Max $" value="${esc(st.max)}" data-f-max aria-label="Maximum price"></div>
        </details>`;
      // keep focus in a facet find box while typing
      if (opts.focusFind) { const f = $(`[data-f-find="${opts.focusFind}"]`, els.facets); if (f) { f.focus(); f.setSelectionRange(f.value.length, f.value.length); } }

      // chips
      const chips = [];
      FACETS.forEach((fc) => (st.f[fc.key] || new Set()).forEach((v) => chips.push(`<button type="button" class="chip" data-f-unset="${fc.key}" data-value="${esc(v)}">${esc(fc.label)}: ${esc(v)} ✕</button>`)));
      if (st.min || st.max) chips.push(`<button type="button" class="chip" data-f-unset="price">Price: ${st.min ? '$' + esc(st.min) : '$0'} – ${st.max ? '$' + esc(st.max) : 'any'} ✕</button>`);
      if (chips.length) chips.push('<button type="button" class="chip is-active" data-f-reset>Clear all</button>');
      els.chips.innerHTML = chips.join('');
      els.nactive.hidden = !chips.length;
      els.nactive.textContent = Math.max(0, chips.length - 1);
      els.clearq.hidden = !st.q;

      // note: suggestions / empty states
      if (!results.length && st.q) {
        els.note.hidden = false;
        els.note.innerHTML = `<strong>No cards match “${esc(st.q)}”.</strong>${suggestion ? ` Did you mean <button type="button" class="linklike" data-f-suggest="${esc(suggestion)}">${esc(suggestion)}</button>?` : ' Try a shorter name or a set code like OP14 or OBF.'}`;
      } else if (loose && st.q) {
        els.note.hidden = false;
        els.note.innerHTML = `No cards are named “${esc(st.q)}”${suggestion ? ` — did you mean <button type="button" class="linklike" data-f-suggest="${esc(suggestion)}">${esc(suggestion)}</button>?` : '.'} Showing close matches instead.`;
      } else if (!filtered.length && results.length) {
        els.note.hidden = false;
        els.note.innerHTML = `No cards match these filters. <button type="button" class="linklike" data-f-reset>Clear filters</button>${st.stock ? ' or <button type="button" class="linklike" data-f-outofstock>include sold-out cards</button>' : ''}.`;
      } else els.note.hidden = true;

      // count
      els.count.textContent = `${filtered.length.toLocaleString()} ${filtered.length === 1 ? 'result' : 'results'}${st.q ? ` for “${st.q}”` : ''}`;

      // page
      const pages = Math.max(1, Math.ceil(filtered.length / perPage));
      if (st.page > pages) st.page = pages;
      const slice = filtered.slice((st.page - 1) * perPage, st.page * perPage);
      const isList = st.view === 'list';
      els.grid.dataset.view = isList ? 'list' : 'grid';
      els.grid.innerHTML = slice.map((it, i) => cardHTML(it, { eager: i < 4, list: isList, showGame: !root.dataset.lockGame })).join('');
      afterCards(els.grid);
      $$('[data-f-view]', root).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.fView === st.view)));

      // pagination
      if (pages > 1) {
        const nums = [];
        const add = (n) => nums.push(n === st.page ? `<span class="current" aria-current="page">${n}</span>` : `<a href="#" data-f-page="${n}">${n}</a>`);
        const gap = () => nums.push('<span class="gap">…</span>');
        if (st.page > 1) nums.push(`<a href="#" data-f-page="${st.page - 1}" aria-label="Previous page">‹</a>`);
        for (let n = 1; n <= pages; n++) {
          if (n === 1 || n === pages || Math.abs(n - st.page) <= 2) add(n);
          else if (n === 2 || n === pages - 1) gap();
        }
        if (st.page < pages) nums.push(`<a href="#" data-f-page="${st.page + 1}" aria-label="Next page">›</a>`);
        els.pages.innerHTML = nums.join('');
        els.pages.hidden = false;
      } else { els.pages.innerHTML = ''; els.pages.hidden = true; }

      writeState(root, st);
      if (opts.scroll) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // ---- events ----
    let qt;
    els.q.addEventListener('input', () => { clearTimeout(qt); qt = setTimeout(() => { st.q = els.q.value.trim(); st.page = 1; render(); }, 120); });
    $('[data-f-form]', root).addEventListener('submit', (e) => { e.preventDefault(); st.q = els.q.value.trim(); st.page = 1; render(); els.q.blur(); });
    els.clearq.addEventListener('click', () => { els.q.value = ''; st.q = ''; st.page = 1; render(); els.q.focus(); });
    els.sort.addEventListener('change', () => { st.sort = els.sort.value; st.page = 1; render(); });
    els.stock.addEventListener('change', () => { st.stock = els.stock.checked; st.page = 1; render(); });

    root.addEventListener('change', (e) => {
      const cb = e.target.closest('[data-f-facet]');
      if (cb) {
        const k = cb.dataset.fFacet;
        st.f[k] = st.f[k] || new Set();
        if (cb.checked) st.f[k].add(cb.value); else st.f[k].delete(cb.value);
        if (k === 'game' && st.f.set) st.f.set.clear();
        st.page = 1; render(); return;
      }
      if (e.target.matches('[data-f-min], [data-f-max]')) {
        st.min = $('[data-f-min]', root).value; st.max = $('[data-f-max]', root).value; st.page = 1; render();
      }
    });
    root.addEventListener('input', (e) => {
      const f = e.target.closest('[data-f-find]');
      if (f) { facetFind[f.dataset.fFind] = f.value; render({ focusFind: f.dataset.fFind }); }
    });
    root.addEventListener('toggle', (e) => {
      const d = e.target.closest && e.target.closest('details[data-facet]');
      if (d) { if (d.open) openFacets.add(d.dataset.facet); else openFacets.delete(d.dataset.facet); }
    }, true);
    root.addEventListener('click', (e) => {
      const t = e.target;
      const pg = t.closest('[data-f-page]');
      if (pg) { e.preventDefault(); st.page = Number(pg.dataset.fPage); render({ scroll: true }); return; }
      const un = t.closest('[data-f-unset]');
      if (un) {
        if (un.dataset.fUnset === 'price') { st.min = ''; st.max = ''; } else if (st.f[un.dataset.fUnset]) st.f[un.dataset.fUnset].delete(un.dataset.value);
        st.page = 1; render(); return;
      }
      if (t.closest('[data-f-reset]')) { st.f = {}; st.min = ''; st.max = ''; st.page = 1; render(); return; }
      if (t.closest('[data-f-outofstock]')) { st.stock = false; els.stock.checked = false; render(); return; }
      const sg = t.closest('[data-f-suggest]');
      if (sg) { st.q = sg.dataset.fSuggest; els.q.value = st.q; st.page = 1; render(); return; }
      const more = t.closest('[data-f-more]');
      if (more) { showAll.add(more.dataset.fMore); render(); return; }
      const pc = t.closest('[data-f-price]');
      if (pc) {
        const [a, b] = pc.dataset.fPrice.split('|');
        const same = pc.classList.contains('is-active');
        st.min = same || a === '' ? '' : String(Number(a) / 100);
        st.max = same || b === '' ? '' : String(Number(b) / 100);
        st.page = 1; render(); return;
      }
      const vw = t.closest('[data-f-view]');
      if (vw) { st.view = vw.dataset.fView; try { localStorage.setItem('ar:finder:view', st.view); } catch (err) { /* ignore */ } render(); return; }
      if (t.closest('[data-f-open]')) { els.side.classList.add('is-open'); root.classList.add('side-open'); document.body.classList.add('no-scroll'); return; }
      if (t.closest('[data-f-close]')) { els.side.classList.remove('is-open'); root.classList.remove('side-open'); document.body.classList.remove('no-scroll'); }
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && root.classList.contains('side-open')) { els.side.classList.remove('is-open'); root.classList.remove('side-open'); document.body.classList.remove('no-scroll'); } });

    render();
    if (isSearch && !st.q && root.dataset.autofocus === '1') els.q.focus();
  }

  /* ---------- header / hero instant suggestions ---------- */
  function suggestHTML(items, q, searchUrl) {
    const { results, scores } = search(items, q);
    const cmp = sorter('relevance', scores);
    if (cmp) results.sort(cmp);
    const nq = norm(q);
    const sets = new Map();
    items.forEach((it) => { if (it.set && (norm(it.set).includes(nq) || norm(it.code) === nq)) sets.set(it.set, (sets.get(it.set) || 0) + 1); });
    const games = getLookups().gameOrder.filter((g) => norm(g).includes(nq));
    const su = (k, v) => `${searchUrl}?${k}=${encodeURIComponent(v)}`;
    const top = results.slice(0, 7);
    let html = '';
    if (games.length) html += `<div class="suggest__group"><span class="suggest__label">Games</span>${games.slice(0, 3).map((g) => `<a class="suggest__chip" href="${su('game', g)}">${esc(g)}</a>`).join('')}</div>`;
    if (sets.size) html += `<div class="suggest__group"><span class="suggest__label">Sets</span>${Array.from(sets).slice(0, 4).map(([s, n]) => `<a class="suggest__chip" href="${su('set', s)}">${esc(s)} <small>${n}</small></a>`).join('')}</div>`;
    html += top.map((it) => `<a class="predictive__item" href="${esc(it.url)}">${it.img ? `<img src="${esc(it.img)}" alt="" loading="lazy">` : '<span></span>'}<span><strong>${esc(it.name)}</strong><small class="muted">${esc([it.game, it.code && it.num ? it.code + ' · ' + it.num : it.set, it.print !== 'Normal' ? it.print : ''].filter(Boolean).join(' · '))}${it.avail ? '' : ' · Sold out'}</small></span><span class="price">${money(it.p)}</span></a>`).join('');
    if (!top.length && !sets.size && !games.length) html += `<p class="muted suggest__empty">No cards match “${esc(q)}”.</p>`;
    html += `<a class="predictive__all" href="${searchUrl}?q=${encodeURIComponent(q)}">See all ${results.length.toLocaleString()} results →</a>`;
    return html;
  }

  /* ---------- public API ---------- */
  window.RiftFinder = { fetchFeed, loadAll, search, sorter, enrich, cardHTML, afterCards, suggestHTML, norm, money, condCode, mount };

  function boot() { $$('[data-finder]').forEach(mount); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
